/**
 * Adapter between the Beecher entity wizard's stored step objects and the PML 2025 engine.
 *
 * Framework-free (no Angular). Two jobs:
 *   1. `readWizardPmlInput()` — pull the engine inputs out of the wizard's `financial`, `dataScope` and
 *      `technical` step arrays (same indexes the v1 formulas use).
 *   2. `applyPml2025ToRiskScenarios()` — write an engine result back into the wizard's `riskScenarios`
 *      sections so every downstream reader (upperdeck, risk table, Excel report) keeps working unchanged:
 *      they read `inherent` / `residual` / `moreChildren[].{name,value}` by section index.
 *
 * The model selection itself is persisted as `riskScenarios[2].pmlModelVersion` (the Hybrid section object).
 * Extra keys on that object are ignored by every existing reader. We deliberately do NOT add a fourth array
 * element: upperdeck iterates the array by index.
 */
import { PML_MODEL_VERSION_2021, PML_MODEL_VERSION_2025 } from './constants';
import { Pml2025Input, Pml2025Result, PmlModelVersion, PmlRecordCounts, PmlWarning } from './types';

export interface WizardLineItem {
  name: string;
  value: number;
  additionalChild?: any;
}

export interface WizardPmlStamp {
  pmlModelVersion: PmlModelVersion;
  pmlTableVersion: string;
  pmlCalculatedAt: string;
  warnings: PmlWarning[];
}

export interface WizardRiskSection {
  title: string;
  inherent: number;
  residual: number;
  moreChildren: WizardLineItem[];
  /** Only ever set on section index 2 (Hybrid). */
  pmlModelVersion?: PmlModelVersion;
  pmlStamp?: WizardPmlStamp;
}

export interface WizardPmlModelOption {
  value: PmlModelVersion;
  label: string;
}

export const WIZARD_PML_MODEL_OPTIONS: WizardPmlModelOption[] = [
  { value: PML_MODEL_VERSION_2021, label: 'Current (v1)' },
  { value: PML_MODEL_VERSION_2025, label: '2025 (v2)' },
];

const SECTION_TITLES = ['Data Breach', 'Business Interruption', 'Hybrid'];
const FLAG_SECTION_INDEX = 2;

/**
 * Wizard fields the 2025 model does not read (spec §4 / Addendum B). While v2 is active they stay visible with
 * their defaults but are locked: greyed out, not editable, and their mandatory marker is not enforced.
 * "Replacement of Hardware" is auto-computed by the wizard (servers × cost + workstations × cost); v2 computes the
 * same product internally, so the shown value stays consistent while locked.
 */
export const PML_2025_UNUSED_FIELD_TITLES: string[] = [
  'Cost Per Employee',
  'Cost Per Teammate',
  'Property and Equipment',
  'Percent of Equipment Budget',
  'Crown Jewel IT Assets',
  'Replacement of Hardware',
  'Forensic and Remediation',
  'PR and Response Expenses',
  'Annual Selling and Administration',
  'Other Expenses',
];

export function isPml2025UnusedField(title: string | undefined): boolean {
  return !!title && PML_2025_UNUSED_FIELD_TITLES.includes(title);
}

/**
 * Human-readable model label for reports. Decided by the STAMP, not the flag: the stamp is written only when
 * v2 numbers were actually stored, so a flagged record saved on a non-Beecher host still reads as v1.
 */
export function describePmlModelForReport(riskScenarios: WizardRiskSection[] | null | undefined): string {
  const stamp = riskScenarios && riskScenarios[FLAG_SECTION_INDEX] ? riskScenarios[FLAG_SECTION_INDEX].pmlStamp : null;
  if (stamp && stamp.pmlModelVersion === PML_MODEL_VERSION_2025) {
    const when = stamp.pmlCalculatedAt ? ` · calculated ${stamp.pmlCalculatedAt.slice(0, 10)}` : '';
    return `PML model: 2025 (v2) · tables ${stamp.pmlTableVersion}${when}`;
  }
  return 'PML model: Current (v1)';
}

// ---------------------------------------------------------------------------
// Model selection (the per-tenant flag)
// ---------------------------------------------------------------------------

/** What the record says. Absent / unknown → v1. */
export function readStoredPmlModelVersion(riskScenarios: WizardRiskSection[] | null | undefined): PmlModelVersion {
  const stored =
    riskScenarios && riskScenarios[FLAG_SECTION_INDEX] && riskScenarios[FLAG_SECTION_INDEX].pmlModelVersion;
  return stored === PML_MODEL_VERSION_2025 ? PML_MODEL_VERSION_2025 : PML_MODEL_VERSION_2021;
}

/**
 * What actually runs. Both gates must pass: the host must be a Beecher host AND the record must be flagged.
 * A flagged record opened on a non-Beecher host silently computes v1.
 */
export function resolvePmlModelVersion(
  riskScenarios: WizardRiskSection[] | null | undefined,
  hostAllowed: boolean
): PmlModelVersion {
  return hostAllowed ? readStoredPmlModelVersion(riskScenarios) : PML_MODEL_VERSION_2021;
}

export function setStoredPmlModelVersion(riskScenarios: WizardRiskSection[], version: PmlModelVersion): void {
  ensureSections(riskScenarios);
  riskScenarios[FLAG_SECTION_INDEX].pmlModelVersion = version;
  if (version !== PML_MODEL_VERSION_2025) {
    delete riskScenarios[FLAG_SECTION_INDEX].pmlStamp;
  }
}

// ---------------------------------------------------------------------------
// Wizard step objects → engine input
// ---------------------------------------------------------------------------

function numberAt(list: any[] | undefined, index: number): number {
  const v = list && list[index] ? list[index].value : 0;
  return typeof v === 'number' && isFinite(v) && v > 0 ? v : 0;
}

/**
 * Record counts, internal + external summed, checked rows only — identical selection rule to the v1
 * `getDataScopeValues()`. Unchecked or empty types are 0 (Addendum B §B6).
 */
export function readWizardRecordCounts(dataScope: any[]): PmlRecordCounts {
  const records: PmlRecordCounts = { PHI: 0, PFI: 0, PII: 0, PCI: 0 };
  (dataScope || []).forEach((group, index) => {
    if (index >= 2 || !Array.isArray(group)) {
      return;
    }
    group.forEach(prop => {
      if (!prop || !prop.isChecked || !(prop.value > 0)) {
        return;
      }
      const title: string = prop.title || '';
      if (title.includes('PIFI')) {
        records.PFI += prop.value;
      } else if (title.includes('PII')) {
        records.PII += prop.value;
      } else if (title.includes('PHI')) {
        records.PHI += prop.value;
      } else if (title.includes('PCI')) {
        records.PCI += prop.value;
      }
    });
  });
  return records;
}

/**
 * Field positions are the ones the v1 formulas already rely on:
 *   financial[0][0] Annual Revenue · financial[0][1] Annual Cost of Goods and/or Services
 *   technical[0][0] Number of Servers · [0][1] Number of Workstations · [0][3] Cost Per Server · [0][4] Cost Per Workstation
 */
export function readWizardPmlInput(
  financial: any[],
  dataScope: any[],
  technical: any[],
  industry?: string
): Pml2025Input {
  const fin = financial && financial[0];
  const tech = technical && technical[0];
  return {
    revenue: numberAt(fin, 0),
    cogs: numberAt(fin, 1),
    industry: industry || undefined,
    numServers: numberAt(tech, 0),
    costPerServer: numberAt(tech, 3),
    numWorkstations: numberAt(tech, 1),
    costPerWorkstation: numberAt(tech, 4),
    records: readWizardRecordCounts(dataScope),
  };
}

// ---------------------------------------------------------------------------
// Engine result → wizard riskScenarios sections
// ---------------------------------------------------------------------------

type LineItemMap = Array<[string, number]>;

/** Data Breach line items (spec §5). The 2025 model has one regulatory-fines figure, not per-agency splits. */
export function dataBreachLineItems(result: Pml2025Result): LineItemMap {
  const a = result.dataBreach.allRecords;
  return [
    ['Forensic Investigation', a.FORENSICS_COST],
    ['Data Breach Coach', a.BREACH_COACH_COST],
    ['Notification Letters', a.NOTIFICATIONS_COST],
    ['Call Center', a.CALL_CENTER_COST],
    ['Credit Monitoring', a.CREDIT_MONITORING_COST],
    ['Public Relations Service', a.PUBLIC_RELATIONS_COST],
    ['Regulatory Fines', a.SIM_REGULATORY_FINES],
    ['Civil Damages', a.SIM_CIVIL_COST],
    ['Legal Defense', a.SIM_DEFENSE_COSTS],
    ['Bank Card Reissuance', a['BANK CARD_REISS']],
  ];
}

/** Business Interruption line items (spec §6.1–6.3). Same three titles as v1. */
export function businessInterruptionLineItems(result: Pml2025Result): LineItemMap {
  const bi = result.businessInterruption;
  return [
    ['Income Loss', bi.incomeLoss],
    ['System Reconstruction', bi.systemReconstruction],
    ['Extra Expense', bi.extraExpense],
  ];
}

/** Hybrid line items (spec §6.4–6.5). They sum exactly to `hybrid.total`, the headline PML. */
export function hybridLineItems(result: Pml2025Result): LineItemMap {
  const h = result.hybrid;
  const a = result.dataBreach.allRecords;
  return [
    ['Ransomware', h.ransomwarePayment],
    ['Forensic Investigation', h.forensics],
    ['Data Breach Coach', h.breachCoach],
    ['Public Relations Service', h.publicRelations],
    ['Notification Letters', a.NOTIFICATIONS_COST],
    ['Call Center', a.CALL_CENTER_COST],
    ['Credit Monitoring', a.CREDIT_MONITORING_COST],
    ['Bank Card Reissuance', a['BANK CARD_REISS']],
    ['Regulatory Fines', a.SIM_REGULATORY_FINES],
    ['Civil Damages', a.SIM_CIVIL_COST],
    ['Legal Defense', h.legalDefense],
    ['Income Loss', h.incomeLoss],
    ['System Reconstruction', h.systemReconstruction],
  ];
}

function ensureSections(riskScenarios: WizardRiskSection[]): void {
  SECTION_TITLES.forEach((title, index) => {
    if (!riskScenarios[index]) {
      riskScenarios[index] = { title, inherent: 0, residual: 0, moreChildren: [] };
    }
    if (!Array.isArray(riskScenarios[index].moreChildren)) {
      riskScenarios[index].moreChildren = [];
    }
  });
}

function writeSection(section: WizardRiskSection, items: LineItemMap, total: number): void {
  section.moreChildren = items.map(([name, value]) => ({ name, value }));
  section.inherent = total;
  section.residual = total;
}

/**
 * Writes a v2 result into the three wizard sections and stamps the record. Returns the headline PML
 * (Hybrid total), which is what the Beecher wizard shows as "Total Inherent Risk".
 *
 * Line-item titles are replaced wholesale, so switching back to v1 must rebuild the v1 title lists first
 * (the wizard does that from its own defaults).
 */
export function applyPml2025ToRiskScenarios(riskScenarios: WizardRiskSection[], result: Pml2025Result): number {
  ensureSections(riskScenarios);
  writeSection(riskScenarios[0], dataBreachLineItems(result), result.dataBreach.total);
  writeSection(riskScenarios[1], businessInterruptionLineItems(result), result.businessInterruption.total);
  writeSection(riskScenarios[2], hybridLineItems(result), result.hybrid.total);
  riskScenarios[FLAG_SECTION_INDEX].pmlModelVersion = PML_MODEL_VERSION_2025;
  riskScenarios[FLAG_SECTION_INDEX].pmlStamp = {
    pmlModelVersion: result.pmlModelVersion,
    pmlTableVersion: result.pmlTableVersion,
    pmlCalculatedAt: result.pmlCalculatedAt,
    warnings: result.warnings,
  };
  return result.hybrid.total;
}
